// Copyright (c) 2023 WiseTime. All rights reserved.

import { concat, EMPTY, forkJoin, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, mergeMap, withLatestFrom } from "rxjs/operators"
import { t } from "@lingui/macro"
import { create } from "@bufbuild/protobuf"
import { showSnackBar } from "../../snackbar/actions"
import * as Actions from "../actions"
import * as TimelineSlice from "../slices/timeline"
import * as UserContextSlice from "../../user-context/slice"
import type { RootState } from "../../../app/store"
import {
  Code,
  executor,
  executorMutation,
  Init,
  isConnectError,
  timelineService,
  timesheetFieldService,
} from "../../../app/rpc"
import {
  GenerateTimesheetFieldConfigsRequestSchema,
  GenerateTimesheetFieldConfigsResponse,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"
import { CreateEmptyTimeGroupRequestSchema, TimeGroup } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

export const getLastActiveCell = (timeGroup: TimeGroup): TimelineSlice.Cell => {
  const hasTags = timeGroup.tags.length !== 0
  const hasDuration = timeGroup.totalDuration !== undefined
    && (Number(timeGroup.totalDuration.seconds) > 0 || timeGroup.totalDuration.nanos > 0)
  const noData = !hasTags
    && !hasDuration
    && timeGroup.narrative.length === 0
  if (noData) {
    return TimelineSlice.Cell.TIME_ROWS
  }
  if (hasTags) {
    return TimelineSlice.Cell.TAG
  }
  if (hasDuration) {
    return TimelineSlice.Cell.DURATION
  }
  return TimelineSlice.Cell.NARRATIVE
}

type CreateEmptyTimeGroupRpc = (request: Init<typeof CreateEmptyTimeGroupRequestSchema>) => Observable<TimeGroup>
type GenerateTimesheetFieldConfigsRpc = (request: Init<typeof GenerateTimesheetFieldConfigsRequestSchema>) => Observable<GenerateTimesheetFieldConfigsResponse>

export const createEmptyTimeGroup = (
  createEmptyTimeGroupRpc: CreateEmptyTimeGroupRpc,
  generateTimesheetFieldConfigsRpc: GenerateTimesheetFieldConfigsRpc,
) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.createEmptyTimeGroup.match),
      withLatestFrom(state$),
      mergeMap(([{ payload }, state]) => {
        const currentSiteId = UserContextSlice.selectCurrentSiteId(state)
        const timesheetFieldConfigsCache = TimelineSlice.selectTimesheetFieldConfigsCache(state)
        const pendingTimesheetFieldConfigRequests = TimelineSlice.selectPendingTimesheetFieldConfigRequests(state)
        const tagId = (payload.request.tags ?? [])
          .filter(t => t.siteId === currentSiteId)?.[0]?.id ?? ""
        const generateTimesheetFieldConfigs = currentSiteId ? generateTimesheetFieldConfigsRpc({
          siteId: currentSiteId,
          tagId,
        }).pipe(
          catchError(() => of(undefined))
        ) : of(undefined)
        return forkJoin([
          createEmptyTimeGroupRpc(payload.request),
          generateTimesheetFieldConfigs,
        ])
          .pipe(
            mergeMap(([timeGroupFromResponse, generateTimesheetFieldConfigsResponse]) => {
              const timeGroup = timeGroupFromResponse
              const configRefsList = generateTimesheetFieldConfigsResponse?.configRefs ?? []
              const nonCachedConfigRefsList = configRefsList
                .filter(configRef => (
                  timesheetFieldConfigsCache[configRef.configHash] === undefined
                  && !pendingTimesheetFieldConfigRequests.includes(configRef.configHash)
                ))
              const {
                onSuccess,
                createTimeGroupRowIndex,
                skipSelection,
              } = payload
              if (onSuccess) {
                onSuccess()
              }
              const isCreatedFromEmptyRow = createTimeGroupRowIndex !== undefined
              const optionalRemoveFromCreateRowIndexesList = isCreatedFromEmptyRow ?
                of(TimelineSlice.removeFromCreateRowIndexesList(createTimeGroupRowIndex)) : EMPTY
              const optionalSetTimeGroupFieldConfigs = configRefsList.length !== 0 ?
                of(TimelineSlice.setTimeGroupFieldConfigs({
                  timeGroupId: timeGroup.id,
                  configHashList: configRefsList.map(configRef => configRef.configHash),
                })) : EMPTY
              const optionalBatchGetTimesheetFieldConfigs = nonCachedConfigRefsList.length && currentSiteId ? of(
                Actions.batchGetTimesheetFieldConfigs({
                  siteId: currentSiteId,
                  configRefs: nonCachedConfigRefsList.map(configRef => ({
                    timesheetFieldId: configRef.timesheetFieldId,
                    configHash: configRef.configHash,
                  })),
                })
              ) : EMPTY
              return concat(
                of(TimelineSlice.upsertTimeGroup(timeGroup)),
                optionalBatchGetTimesheetFieldConfigs,
                optionalSetTimeGroupFieldConfigs,
                optionalRemoveFromCreateRowIndexesList,
                of(TimelineSlice.setKeepTabOrder({
                  timeGroupId: timeGroup.id,
                  cell: getLastActiveCell(timeGroup),
                })),
                of(TimelineSlice.replaceActiveTimeGroupsIds({
                  timeGroupsIds: skipSelection ? [] : [timeGroup.id],
                })),
              )
            }),
            catchError((e) => handleError(e, payload)),
          )
      })
    )

const handleError = (e: unknown, payload: Actions.CreateEmptyTimeGroupPayload) => {
  if (payload.onError) {
    payload.onError()
  }
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.PermissionDenied) {
      return of(showSnackBar({
        message: t({ id: "msg_create_empty_group_permission_denied", message: "Failed to create timesheet. You do not have permission to perform this action." }),
        messageType: "ERROR",
      }))
    }
  }
  return of(showSnackBar({
    message: t({ id: "msg_create_time_group_failed", message: "Failed to create the timesheet. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

const createEmptyTimeGroupRpc = (request: Init<typeof CreateEmptyTimeGroupRequestSchema>): Observable<TimeGroup> =>
  executorMutation(() => timelineService.createEmptyTimeGroup(create(CreateEmptyTimeGroupRequestSchema, request)))
const generateTimesheetFieldConfigsRpc = (request: Init<typeof GenerateTimesheetFieldConfigsRequestSchema>): Observable<GenerateTimesheetFieldConfigsResponse> =>
  executor(() => timesheetFieldService.generateTimesheetFieldConfigs(create(GenerateTimesheetFieldConfigsRequestSchema, request)))

export default createEmptyTimeGroup(createEmptyTimeGroupRpc, generateTimesheetFieldConfigsRpc)
